import { isClose } from '@utils';
import { z } from 'zod';
import { BasicColorSchema, Color, COLORS_BY_NAME } from './Color';
import { ColorMode } from './ColorMode';
import { type IColor } from './IColor';

export function colorsEqual(a: IColor | undefined, b: IColor | undefined): boolean {
  if (a === undefined) {
    return b === undefined;
  }
  if (b === undefined) {
    return false;
  }
  if (a instanceof Color && b instanceof Color) {
    return a.equals(b);
  }
  if (a instanceof AdaptiveColor && b instanceof AdaptiveColor) {
    return a.equals(b);
  }
  return false;
}

export class AdaptiveColor implements IColor {
  #light: Color;
  #dark: Color;

  constructor(light: Color, dark: Color) {
    this.#light = light;
    this.#dark = dark;
  }

  get light(): Color {
    return this.#light;
  }

  get dark(): Color {
    return this.#dark;
  }

  realize(mode: ColorMode): Color {
    return mode === ColorMode.LIGHT ? this.light : this.dark;
  }

  equals(other: AdaptiveColor): boolean {
    return this.light.equals(other.light) && this.dark.equals(other.dark);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  lerp(t: number, end: IColor): AdaptiveColor {
    throw new Error('Cannot interpolate unrealized adaptive color');
  }

  static forId(id: string): AdaptiveColor | undefined {
    return ADAPTIVE_COLORS_BY_NAME[id];
  }

  /**
   * Returns the name of the closest named adaptive color if this adaptive color is close to one,
   * otherwise returns undefined. Uses O(1) lookup for performance.
   */
  private getClosestAdaptiveColorName(): string | undefined {
    // First check exact match for O(1) performance
    const exactKey = createAdaptiveColorKey(this.light, this.dark);
    const exactMatch = ADAPTIVE_COLOR_LOOKUP.get(exactKey);
    if (exactMatch) {
      return exactMatch;
    }

    // Check if close to any named adaptive color using epsilon comparison
    for (const [name, namedAdaptiveColor] of Object.entries(ADAPTIVE_COLORS_BY_NAME)) {
      if (this.isCloseTo(namedAdaptiveColor)) {
        return name;
      }
    }

    return undefined;
  }

  /**
   * Checks if this adaptive color is close to another adaptive color within epsilon tolerance.
   */
  private isCloseTo(other: AdaptiveColor): boolean {
    return (
      (this.light.red === other.light.red &&
        this.light.green === other.light.green &&
        this.light.blue === other.light.blue &&
        this.light.alpha === other.light.alpha &&
        this.dark.red === other.dark.red &&
        this.dark.green === other.dark.green &&
        this.dark.blue === other.dark.blue &&
        this.dark.alpha === other.dark.alpha) ||
      (isClose(this.light.red, other.light.red) &&
        isClose(this.light.green, other.light.green) &&
        isClose(this.light.blue, other.light.blue) &&
        isClose(this.light.alpha, other.light.alpha) &&
        isClose(this.dark.red, other.dark.red) &&
        isClose(this.dark.green, other.dark.green) &&
        isClose(this.dark.blue, other.dark.blue) &&
        isClose(this.dark.alpha, other.dark.alpha))
    );
  }

  toJSON(): unknown {
    // Check if this adaptive color is close to any named adaptive color
    const adaptiveColorName = this.getClosestAdaptiveColorName();
    if (adaptiveColorName) {
      return adaptiveColorName;
    }

    return {
      light: this.light.toJSON(),
      dark: this.dark.toJSON()
    };
  }

  //
  // User-facing colors
  //

  static FOREGROUND = new AdaptiveColor(Color.BLACK, Color.WHITE);
  static RED = new AdaptiveColor(
    Color.from8Bit(255, 59, 48),
    Color.from8Bit(255, 69, 58)
  );
  static ORANGE = new AdaptiveColor(
    Color.from8Bit(255, 149, 0),
    Color.from8Bit(255, 159, 10)
  );
  static YELLOW = new AdaptiveColor(
    Color.from8Bit(255, 204, 0),
    Color.from8Bit(255, 214, 10)
  );
  static GREEN = new AdaptiveColor(
    Color.from8Bit(40, 205, 65),
    Color.from8Bit(50, 215, 75)
  );
  static MINT = new AdaptiveColor(
    Color.from8Bit(0, 199, 190),
    Color.from8Bit(102, 212, 207)
  );
  static TEAL = new AdaptiveColor(
    Color.from8Bit(89, 173, 196),
    Color.from8Bit(106, 196, 220)
  );
  static CYAN = new AdaptiveColor(
    Color.from8Bit(85, 190, 240),
    Color.from8Bit(90, 200, 245)
  );
  static BLUE = new AdaptiveColor(
    Color.from8Bit(0, 122, 255),
    Color.from8Bit(10, 132, 255)
  );
  static INDIGO = new AdaptiveColor(
    Color.from8Bit(88, 86, 214),
    Color.from8Bit(94, 92, 230)
  );
  static PURPLE = new AdaptiveColor(
    Color.from8Bit(175, 82, 222),
    Color.from8Bit(191, 90, 242)
  );
  static PINK = new AdaptiveColor(
    Color.from8Bit(255, 45, 85),
    Color.from8Bit(255, 55, 95)
  );
  static BROWN = new AdaptiveColor(
    Color.from8Bit(162, 132, 94),
    Color.from8Bit(172, 142, 104)
  );
  static GRAY = new AdaptiveColor(
    Color.from8Bit(142, 142, 147),
    Color.from8Bit(152, 152, 157)
  );
  static BLACK = new AdaptiveColor(Color.BLACK, Color.BLACK);
  static WHITE = new AdaptiveColor(Color.WHITE, Color.WHITE);

  //
  // UI colors
  //

  static GRAY_2 = new AdaptiveColor(
    Color.from8Bit(174, 174, 178),
    Color.from8Bit(99, 99, 102)
  );
  static GRAY_3 = new AdaptiveColor(
    Color.from8Bit(199, 199, 204),
    Color.from8Bit(72, 72, 74)
  );
  static GRAY_4 = new AdaptiveColor(
    Color.from8Bit(209, 209, 214),
    Color.from8Bit(58, 58, 60)
  );
  static GRAY_5 = new AdaptiveColor(
    Color.from8Bit(229, 229, 234),
    Color.from8Bit(44, 44, 46)
  );
  static GRAY_6 = new AdaptiveColor(
    Color.from8Bit(242, 242, 247),
    Color.from8Bit(28, 28, 30)
  );

  static SECONDARY_FOREGROUND = new AdaptiveColor(
    Color.from8Bit(28, 28, 30),
    Color.from8Bit(242, 242, 247)
  );
  static TERTIARY_FOREGROUND = new AdaptiveColor(
    Color.from8Bit(44, 44, 46),
    Color.from8Bit(229, 229, 234)
  );

  static FILL = new AdaptiveColor(
    Color.from8Bit(228, 228, 230),
    Color.from8Bit(206, 206, 209)
  );
  static SECONDARY_FILL = new AdaptiveColor(
    Color.from8Bit(233, 233, 235),
    Color.from8Bit(212, 212, 214)
  );
  static TERTIARY_FILL = new AdaptiveColor(
    Color.from8Bit(239, 239, 240),
    Color.from8Bit(222, 222, 224)
  );
  static QUATERNARY_FILL = new AdaptiveColor(
    Color.from8Bit(244, 244, 245),
    Color.from8Bit(230, 230, 232)
  );

  static BACKGROUND = new AdaptiveColor(Color.WHITE, Color.BLACK);
  static SECONDARY_BACKGROUND = new AdaptiveColor(
    Color.from8Bit(242, 242, 247),
    Color.from8Bit(28, 28, 30)
  );
  static TERTIARY_BACKGROUND = new AdaptiveColor(
    Color.from8Bit(211, 211, 216),
    Color.from8Bit(44, 44, 46)
  );

  static GROUPED_BACKGROUND = new AdaptiveColor(
    Color.from8Bit(242, 242, 247),
    Color.BLACK
  );
  static SECONDARY_GROUPED_BACKGROUND = new AdaptiveColor(
    Color.WHITE,
    Color.from8Bit(28, 28, 30)
  );
  static TERTIARY_GROUPED_BACKGROUND = new AdaptiveColor(
    Color.from8Bit(242, 242, 247),
    Color.from8Bit(44, 44, 46)
  );

  //
  // Debug colors
  //

  static FALSE_COLOR = new AdaptiveColor(Color.RED, Color.BLUE);
}

// Schema for AdaptiveColor JSON format (using the atomic schema for light and dark)
// We need a schema that returns Color objects, not just IColor
const BasicOrNamedColorSchema = z.union([
  BasicColorSchema,
  z.string().transform((name, ctx): Color => {
    // Check for static Color constants
    if (name in COLORS_BY_NAME) {
      return COLORS_BY_NAME[name as keyof typeof COLORS_BY_NAME];
    }

    // Add validation issue instead of throwing
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Unknown basic color name: ${name}`
    });
    return z.NEVER;
  })
]);

export const AdaptiveColorSchema = z
  .object({
    light: BasicOrNamedColorSchema,
    dark: BasicOrNamedColorSchema
  })
  .transform(obj => {
    return new AdaptiveColor(obj.light, obj.dark);
  });

export const ADAPTIVE_FOREGROUND = 'adaptive-foreground';
export const ADAPTIVE_RED = 'adaptive-red';
export const ADAPTIVE_ORANGE = 'adaptive-orange';
export const ADAPTIVE_YELLOW = 'adaptive-yellow';
export const ADAPTIVE_GREEN = 'adaptive-green';
export const ADAPTIVE_MINT = 'adaptive-mint';
export const ADAPTIVE_TEAL = 'adaptive-teal';
export const ADAPTIVE_CYAN = 'adaptive-cyan';
export const ADAPTIVE_BLUE = 'adaptive-blue';
export const ADAPTIVE_INDIGO = 'adaptive-indigo';
export const ADAPTIVE_PURPLE = 'adaptive-purple';
export const ADAPTIVE_PINK = 'adaptive-pink';
export const ADAPTIVE_BROWN = 'adaptive-brown';
export const ADAPTIVE_GRAY = 'adaptive-gray';
export const ADAPTIVE_BLACK = 'adaptive-black';
export const ADAPTIVE_WHITE = 'adaptive-white';
export const ADAPTIVE_GRAY_2 = 'adaptive-gray-2';
export const ADAPTIVE_GRAY_3 = 'adaptive-gray-3';
export const ADAPTIVE_GRAY_4 = 'adaptive-gray-4';
export const ADAPTIVE_GRAY_5 = 'adaptive-gray-5';
export const ADAPTIVE_GRAY_6 = 'adaptive-gray-6';
export const ADAPTIVE_SECONDARY_FOREGROUND = 'adaptive-secondary-foreground';
export const ADAPTIVE_TERTIARY_FOREGROUND = 'adaptive-tertiary-foreground';
export const ADAPTIVE_FILL = 'adaptive-fill';
export const ADAPTIVE_SECONDARY_FILL = 'adaptive-secondary-fill';
export const ADAPTIVE_TERTIARY_FILL = 'adaptive-tertiary-fill';
export const ADAPTIVE_QUATERNARY_FILL = 'adaptive-quaternary-fill';
export const ADAPTIVE_BACKGROUND = 'adaptive-background';
export const ADAPTIVE_SECONDARY_BACKGROUND = 'adaptive-secondary-background';
export const ADAPTIVE_TERTIARY_BACKGROUND = 'adaptive-tertiary-background';
export const ADAPTIVE_GROUPED_BACKGROUND = 'adaptive-grouped-background';
export const ADAPTIVE_SECONDARY_GROUPED_BACKGROUND =
  'adaptive-secondary-grouped-background';
export const ADAPTIVE_TERTIARY_GROUPED_BACKGROUND =
  'adaptive-tertiary-grouped-background';

// Map of AdaptiveColor static constants by name
export const ADAPTIVE_COLORS_BY_NAME: Record<string, AdaptiveColor> = {
  // User-facing colors
  [ADAPTIVE_FOREGROUND]: AdaptiveColor.FOREGROUND,
  [ADAPTIVE_RED]: AdaptiveColor.RED,
  [ADAPTIVE_ORANGE]: AdaptiveColor.ORANGE,
  [ADAPTIVE_YELLOW]: AdaptiveColor.YELLOW,
  [ADAPTIVE_GREEN]: AdaptiveColor.GREEN,
  [ADAPTIVE_MINT]: AdaptiveColor.MINT,
  [ADAPTIVE_TEAL]: AdaptiveColor.TEAL,
  [ADAPTIVE_CYAN]: AdaptiveColor.CYAN,
  [ADAPTIVE_BLUE]: AdaptiveColor.BLUE,
  [ADAPTIVE_INDIGO]: AdaptiveColor.INDIGO,
  [ADAPTIVE_PURPLE]: AdaptiveColor.PURPLE,
  [ADAPTIVE_PINK]: AdaptiveColor.PINK,
  [ADAPTIVE_BROWN]: AdaptiveColor.BROWN,
  [ADAPTIVE_GRAY]: AdaptiveColor.GRAY,
  [ADAPTIVE_BLACK]: AdaptiveColor.BLACK,
  [ADAPTIVE_WHITE]: AdaptiveColor.WHITE,

  // UI colors
  [ADAPTIVE_GRAY_2]: AdaptiveColor.GRAY_2,
  [ADAPTIVE_GRAY_3]: AdaptiveColor.GRAY_3,
  [ADAPTIVE_GRAY_4]: AdaptiveColor.GRAY_4,
  [ADAPTIVE_GRAY_5]: AdaptiveColor.GRAY_5,
  [ADAPTIVE_GRAY_6]: AdaptiveColor.GRAY_6,
  [ADAPTIVE_SECONDARY_FOREGROUND]: AdaptiveColor.SECONDARY_FOREGROUND,
  [ADAPTIVE_TERTIARY_FOREGROUND]: AdaptiveColor.TERTIARY_FOREGROUND,
  [ADAPTIVE_FILL]: AdaptiveColor.FILL,
  [ADAPTIVE_SECONDARY_FILL]: AdaptiveColor.SECONDARY_FILL,
  [ADAPTIVE_TERTIARY_FILL]: AdaptiveColor.TERTIARY_FILL,
  [ADAPTIVE_QUATERNARY_FILL]: AdaptiveColor.QUATERNARY_FILL,
  [ADAPTIVE_BACKGROUND]: AdaptiveColor.BACKGROUND,
  [ADAPTIVE_SECONDARY_BACKGROUND]: AdaptiveColor.SECONDARY_BACKGROUND,
  [ADAPTIVE_TERTIARY_BACKGROUND]: AdaptiveColor.TERTIARY_BACKGROUND,
  [ADAPTIVE_GROUPED_BACKGROUND]: AdaptiveColor.GROUPED_BACKGROUND,
  [ADAPTIVE_SECONDARY_GROUPED_BACKGROUND]: AdaptiveColor.SECONDARY_GROUPED_BACKGROUND,
  [ADAPTIVE_TERTIARY_GROUPED_BACKGROUND]: AdaptiveColor.TERTIARY_GROUPED_BACKGROUND
} as const;

export const ADAPTIVE_COLOR_NAMES = Object.keys(ADAPTIVE_COLORS_BY_NAME);

// Reverse lookup map for O(1) adaptive color name resolution
// Maps adaptive color key (light and dark color keys as string) to adaptive color name
const ADAPTIVE_COLOR_LOOKUP = new Map<string, string>();

// Helper function to create a lookup key for an adaptive color
function createAdaptiveColorKey(lightColor: Color, darkColor: Color): string {
  return `${lightColor.red},${lightColor.green},${lightColor.blue},${lightColor.alpha}|${darkColor.red},${darkColor.green},${darkColor.blue},${darkColor.alpha}`;
}

// Initialize the reverse lookup map
for (const [name, adaptiveColor] of Object.entries(ADAPTIVE_COLORS_BY_NAME)) {
  ADAPTIVE_COLOR_LOOKUP.set(
    createAdaptiveColorKey(adaptiveColor.light, adaptiveColor.dark),
    name
  );
}

export const NamedAdaptiveColorSchema = z.string().transform((name, ctx): IColor => {
  // Check for static AdaptiveColor constants
  if (name in ADAPTIVE_COLORS_BY_NAME) {
    return ADAPTIVE_COLORS_BY_NAME[name];
  }

  // Add validation issue instead of throwing
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Unknown adaptive color name: ${name}`
  });
  return z.NEVER;
});
